import { websiteLocale } from '@formosa/shared/constants/websiteLanguages';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { formatMessage } from '@formosa/shared/i18n/formatMessage';
import { DEFAULT_LOCALE, translate } from '@formosa/shared/i18n/translate';
import { LOCALE_CHANGE_EVENT, LOCALE_STORAGE_KEY, getStoredLocale } from '@formosa/shared/constants/locale';
import { getTravelAdsMessage } from '../i18n/travelAdsMessages';
import { getMeHubMessage } from '../i18n/meHubMessages';
import { getPersonalMapMessage } from '../i18n/personalMapMessages';
import { getPublicationRefreshMessage } from '../i18n/publicationRefreshMessages';
import { getJourneyWorkspaceMessage } from '../i18n/journeyWorkspaceMessages';
import { getPublicationStoryMessage } from '../i18n/publicationStoryMessages';
import { getTravelEntryMessage } from '../i18n/travelEntryMessages';
import { getPlanningFeedbackMessage } from '../i18n/planningFeedbackMessages';
import { getJourneyDiscoveryMessage } from '../i18n/journeyDiscoveryMessages';
import { getChatMessage } from '../i18n/chatMessages';
import { toIntlLocale } from '../i18n/travelFormat';

const TravelLocaleContext = createContext(null);

const FLAT_MESSAGE_LOOKUPS = [
  getChatMessage,
  getJourneyDiscoveryMessage,
  getPersonalMapMessage,
  getMeHubMessage,
  getPublicationRefreshMessage,
  getJourneyWorkspaceMessage,
  getPublicationStoryMessage,
  getTravelEntryMessage,
  getPlanningFeedbackMessage,
];

/** 依序查共用 travel 文案與 Travel 專屬扁平文案表；都沒有時回傳 key。 */
export function translateTravel(locale, key) {
  const primary = translate(locale, key);
  if (primary !== key) return primary;
  for (const lookup of FLAT_MESSAGE_LOOKUPS) {
    const message = lookup(locale, key);
    if (message !== key) return message;
  }
  return getTravelAdsMessage(locale, key);
}

/**
 * 依 app locale 的複數規則取文案：規則為 one 且存在 `${key}_one` 時使用之，否則用 key（other）。
 * 中文的 PluralRules 一律為 other，因此繁簡中文只需提供 key 本身。
 */
function pluralKey(t, intlLocale, key, count) {
  let category = 'other';
  try {
    category = new Intl.PluralRules(intlLocale).select(Number(count));
  } catch {
    category = Number(count) === 1 ? 'one' : 'other';
  }
  if (category === 'one') {
    const oneKey = `${key}_one`;
    if (t(oneKey) !== oneKey) return oneKey;
  }
  return key;
}

function buildTravelText(locale) {
  const intlLocale = toIntlLocale(locale);
  const t = (key) => translateTravel(locale, key);
  const tf = (key, values) => formatMessage(t(key), values);
  const tp = (key, count, values = {}) => formatMessage(t(pluralKey(t, intlLocale, key, count)), { count, ...values });
  return { locale, intlLocale, t, tf, tp };
}

export function TravelLocaleProvider({ children }) {
  const [locale, setLocale] = useState(() => websiteLocale(getStoredLocale() || DEFAULT_LOCALE));

  useEffect(() => {
    function syncLocale(event) {
      const next = event?.detail ?? getStoredLocale();
      setLocale(websiteLocale(next || DEFAULT_LOCALE));
    }

    function onStorage(event) {
      if (event.key === LOCALE_STORAGE_KEY) {
        syncLocale({ detail: event.newValue || '' });
      }
    }

    window.addEventListener(LOCALE_CHANGE_EVENT, syncLocale);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(LOCALE_CHANGE_EVENT, syncLocale);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const value = useMemo(() => buildTravelText(locale), [locale]);
  return <TravelLocaleContext.Provider value={value}>{children}</TravelLocaleContext.Provider>;
}

export function useTravelTranslation() {
  const value = useContext(TravelLocaleContext);
  if (!value) throw new Error('useTravelTranslation must be used inside TravelLocaleProvider');
  return value;
}

export function useOptionalTravelTranslation() {
  return useContext(TravelLocaleContext);
}

const DEFAULT_TRAVEL_TEXT = buildTravelText(DEFAULT_LOCALE);

/** 元件可在未包 Provider 的情境（單元測試、獨立嵌入）下使用；此時固定為預設語系。 */
export function useTravelText() {
  return useContext(TravelLocaleContext) || DEFAULT_TRAVEL_TEXT;
}
